import {
  Box,
  Checkbox, Divider,
  FormControlLabel,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ChangeEvent,
  Fragment,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData, useLocation } from "react-router";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { FormErrors } from "types";
import ErrorIcon from "../../assets/error_circle.svg";
import IntroVideo from "../../assets/OL_login_spalsh.mp4";
import CheckIcon from "../../assets/success_circle.svg";
import {
  Link,
  ButtonText,
  TextInput,
  PasswordInput,
  Spinner
  //Spinner
  // PopUpLogin
} from "../../components";
import { AUTH_MODE, SELF_SIGNUP_TYPE } from "../../consts";
import { useIsLoading } from "../../hooks";
import {  UserMgmtResponse } from "../../services/fetchUserMgmt";
import { useAuthStore, buildSSOUrlWithFromUrl } from "../../stores";
import { getEnvironmentVariables } from "../../utils/general";
import { getLoginType, LoginMode } from "../../utils/login";
import OutputMessage from "../Message/OutputMessage.component";

const { loginMode } = getEnvironmentVariables();
const loginModeTyped = loginMode as LoginMode;

const isModeSSO = () => {
  const loginType = getLoginType(loginModeTyped);
  if (!loginType || loginType === "sso") {
    return true;
  }
  return false;
};

const isModeManual = () => {
  const loginType = getLoginType(loginModeTyped);
  if (!loginType || loginType === "manual") {
    return true;
  }
  return false;
};

export function Login() {
  const theme = useTheme();
  const signIn = useAuthStore(state => state.signIn);
  //const isLoading = useAuthStore(state => state.isLoading);
  const ssoEndpoint = useAuthStore(state => state.ssoEndpoint);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { state: routerState } = useLocation();
  const [searchParams] = useSearchParams();

  // Ottiene fromUrl da router state o query parameters
  const getFromUrl = () => {
    // Prima priorità: router state
    if (routerState?.fromUrl) {
      return routerState.fromUrl;
    }

    // Seconda priorità: query parameters
    const fromUrlParam = searchParams.get("fromUrl");
    if (fromUrlParam) {
      return decodeURIComponent(fromUrlParam);
    }

    return null;
  };

  const fromUrl = getFromUrl();

  const authSSOIsAllowed = useAuthStore(state =>state.allowedAuthentications.includes(AUTH_MODE.SSO));
  const storedUser = localStorage.getItem("storedUser");
  const [username, setUsername] = useState(storedUser ?? "");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [remember, setRemember] = useState(Boolean(storedUser));
  //const isAuthenticated = useAuthStore(state => Boolean(state.session));
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  //const spinnerLoginVisible = useAuthStore(state => state.spinnerLoginVisible);
  //const loadingSpinnerCookie = getCookieByName("loadingSpinner");
  //const setSplash = useAuthStore(state => state.setSplash);
  const registrationCompletedVisible = useAuthStore(state => state.registrationCompletedVisible);
  //const disableSSORedirect = sessionStorage.getItem("disable_sso_redirect");
  const error = useAuthStore(state => state.error);
  const { isLoading, setIsLoading } = useIsLoading();

  const [isTab, setIsTab] = useState(false);
  const [focusIn, setFocusIn] = useState("");

  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const focusedElement = e.target as HTMLElement;
      setFocusIn(focusedElement?.id);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        setIsTab(true);
      }
    };

    const handleMouseDown = () => {
      setIsTab(false);
    };
    document.addEventListener("focusin", handleFocusIn);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);
  const navigate = useNavigate();
  //SMART TV
  //const autoregCode = searchParams?.get("autoreg_code");

  const uuid = searchParams.get("uuid");
  if(uuid){
    localStorage.setItem(
      "uuid_qrcode",
      uuid
    );
  }else{
    localStorage.removeItem("uuid_qrcode");
  }


  const userMgmtData = useLoaderData() as UserMgmtResponse;

  // Verifica se dovrebbe essere attivo l'auto-redirect SSO
  const shouldAutoRedirectSSO = () => {
    return !userMgmtData?.splash &&
           getLoginType(loginModeTyped) === "sso" &&
           authSSOIsAllowed &&
           ssoEndpoint &&
           registrationCompletedVisible === null;
  };

  /* if ( !isAuthenticated && !loadingSpinnerCookie) {
    setSpinnerLoginVisible(false);
  }
    */


  function handleRegister() {
    navigate("/register");
  }

  function handleValidate() {
    /* TODO: use something like yup or zod for validation */

    const passwordIsValid = Boolean(password.length > 0);
    const usernameIsValid = Boolean(username.length > 0);

    setErrors({ password: !passwordIsValid, username: !usernameIsValid });
    return { passwordIsValid, usernameIsValid };
  }

  function handleSubmit() {
    setIsLoading(true);
    const {
      passwordIsValid,
      usernameIsValid
    } = handleValidate();

    if (!passwordIsValid || !usernameIsValid) {
      setIsLoading(false);
      return;
    }

    const payload = {
      password: password,
      username: username
    };
    signIn(userMgmtData.id, payload.username, payload.password, remember, setIsLoading);
  }

  const [showSplash, setShowSplash] = useState((!!userMgmtData.splash) && registrationCompletedVisible === null );

  const autoRedirectSsoLogin = ()=>{
    if(ssoEndpoint){
      const ssoUrl = buildSSOUrlWithFromUrl(ssoEndpoint as URL | string | null, fromUrl);
      if (getLoginType(loginModeTyped) === "sso" && authSSOIsAllowed) {
        window.location.assign(ssoUrl);
      }
    }

  };
  const registerEnabled = (userMgmtData.self_signup === SELF_SIGNUP_TYPE.LEARNER ||
    userMgmtData.self_signup === SELF_SIGNUP_TYPE.MANAGER || userMgmtData.self_signup_enabled);

  let messageError = null;
  if(typeof(error?.message) === "string"){
    messageError = error ? error?.message : null;
  }else{
    messageError = (error &&  error.message ? JSON.parse(error?.message) : null);
  }

  if (showSplash) {
    return <Box sx={ {
      alignItems: "center",
      background: "#000",
      display: "flex",
      inset: "0 0 0 0",
      justifyContent: "center",
      position: "fixed",
      zIndex: 9999
    } }>
      <video style={ {
        height: "100%",
        maxHeight: isMobile ? "100%" : "768px",
        maxWidth: isMobile ? "100%" : "1024px",
        backgroundColor: "black",
        width: "100%"
      } } playsInline={ true } preload={ "auto" } autoPlay={ true } muted={ true }  onEnded={ () =>{
        setShowSplash(false);
        if(ssoEndpoint &&  getLoginType(loginModeTyped) === "sso" && authSSOIsAllowed){
          setSpinnerLoginVisible(true);
          autoRedirectSsoLogin();
        }else{
          setSpinnerLoginVisible(false);
        }
      } }>
        <source src={ IntroVideo } type={ "video/mp4" }/>
      </video>
    </Box>;
  }

  //props OutputRegisterComponent
  const outputRegisterIcon = (registrationCompletedVisible === true) ? CheckIcon : ErrorIcon;
  const labelText = (registrationCompletedVisible === true) ? t("signup.form.success") : t("error_message");
  const descriptionText = (registrationCompletedVisible === true) ?
    t("signup.form.success.description") :
    (messageError?.output?.description ? t(`${messageError.output.description}`) : "");

  const SpinnerLoader = ()=>{
    if(isLoading || shouldAutoRedirectSSO()){
      return <Spinner />;
    }
    return null;
  };

  return (
    <Stack
      role="form"
      alignItems="center"
      gap={ 3 }
      width="430px"
      sx={ {
        [theme.breakpoints.down("sm")]: {
          width: "100%"
        }
      } }
    >
      { SpinnerLoader() ?? (
        (registrationCompletedVisible === null) ?
          <Fragment>
            { /* <PopUpLogin/> */ }
            <Stack
              justifyContent={ isMobile ? "flex-start" : "center" }
              gap={ 1 }
              sx={ {
                [theme.breakpoints.down("sm")]: {
                  width: "100%"
                }
              } }
            >
              <Typography
                sx={ {
                  "&:focus": {
                    outline: "none"
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${theme.customColors.headerIcon}`,
                    outlineOffset: "2px" }
                } }
                color={ theme.customColors.exploreCardText }
                tabIndex={ 0 } 
                aria-label={ t("access_digited") }
                fontSize={ isMobile ? "1.25rem" : "2rem" }
                fontWeight={ 700 }
                lineHeight={ isMobile ? (21 / 20) : (19 / 16) }
                paddingTop={ isMobile ? "16px" : "0" }
                textAlign={ isMobile ? "left" : "center" }
              >
                { t("access_digited") }
              </Typography>
              {
                isModeManual() ? (
                  <Typography
                    sx={ {
                      "&:focus": {
                        outline: "none"
                      },
                      "&:focus-visible": {
                        outline: `2px solid ${theme.customColors.headerIcon}`,
                        outlineOffset: "2px" }
                    } }
                    tabIndex={ 0 }
                    aria-label={ t("enter_credentials") }
                    fontSize={ isMobile ? "0.875rem" : "1.25rem" }
                    lineHeight={ isMobile ? (9 / 7) : (21 / 20) }
                    textAlign={ isMobile ? "left" : "center" }
                  >
                    { t("enter_credentials") }
                  </Typography>
                ) : null
              }
            </Stack>
            {
              isModeManual() ? (
                <Stack component={ "form" } alignItems={ "center" } direction={ "column" } gap={ isMobile ? 3 : 4 } width={ "100%" }>
                  <Stack
                    role="region"
                    aria-label={ t("enter_credentials") }
                    alignItems="center"
                    gap={ isMobile ? 3 : 4 }
                    width="100%"
                    sx={ {
                      paddingTop: "8px"
                    } }
                    onKeyDown={ (e) => {

                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSubmit();
                      }
                    } }
                  >
                    <TextInput
                      error={ errors?.username }
                      helperText={ t("no_valid_username") }
                      id="login-id"
                      label={ t("username") }
                      onChange={ (changeEvent: ChangeEvent<HTMLInputElement>) => {
                        setUsername(changeEvent.target.value);
                      } }
                      size={ isMobile ? "small" : "medium" }
                      value={ username }
                      sx={ { width: "100%",
                        ...(focusIn === "login-id" && isTab && {
                          "& .MuiInputBase-root.Mui-focused": {
                            outline: `2px solid ${theme.palette.text?.primary}`,
                            outlineOffset: "2px"
                          }
                        })
                      } }
                    />
                    <PasswordInput
                      error={ errors?.password }
                      helperText={ t("no_valid_password") }
                      id="login-password"
                      label={ t("password") }
                      onChange={ (changeEvent: ChangeEvent<HTMLInputElement>) => {
                        setPassword(changeEvent.target.value);
                      } }
                      size={ isMobile ? "small" : "medium" }
                      value={ password }
                      sx={ {
                        width: "100%", 
                        ...(focusIn === "login-password" && isTab && {
                          "& .MuiInputBase-root.Mui-focused": {
                            outline: `2px solid ${theme.palette.text?.primary}`,
                            outlineOffset: "2px"
                          }
                        }) ,
                        "& .MuiInputLabel-root": {
                          fontSize: "14px"
                        }
                      } }
                    />
                  </Stack>
                  <Stack
                    role="region"
                    aria-label= { t("recover_pw") }
                    alignItems="center"
                    gap={ 1 }
                    direction="row"
                    justifyContent="space-between"
                    width={ "100%" }
                    marginBottom={ 1 }
                  >
                    <FormControlLabel
                      sx={ {
                        marginLeft: 0,
                        "span": {
                          padding: 0
                        }
                      } }
                      control={
                        <Checkbox
                          inputProps={ { "aria-label": t("remember_credentials") } }
                          // inputRef={ rememberRef }
                          //size={ isMobile ? "small" : "medium" }
                          tabIndex={ 0 }
                          onChange={ (changeEvent) => {
                            setRemember(changeEvent.target.checked);
                          } }
                          checked={ remember }
                          sx={ {
                            color: theme.customColors.textPrimary,
                            margin: "0 0.5rem 0 0",
                            ".MuiSvgIcon-root": {
                              transform: "scale(1.2)",
                              fill: theme.customColors.textPrimary
                            },
                            [theme.breakpoints.down("sm")]: {
                              "svg": {
                                height: "24px",
                                width: "auto"
                              }
                            },
                            "&.Mui-focusVisible": {
                              outline: `2px solid ${theme.palette.text?.primary}`,
                              outlineOffset: "2px",
                              borderRadius: "2px"
                            }
                          } }
                        />
                      }
                      label={
                        <Typography
                          fontSize={ isMobile ? "0.875rem" : "1rem" }
                          lineHeight={ isMobile ? (9 / 7) : (21 / 20) }
                          textTransform="capitalize"
                        >
                          { t("remember_me") }
                        </Typography>
                      }
                    />
                    <Link
                      tabIndex={ 0 }
                      sx={ {
                        textDecoration: "underline",
                        textUnderlineOffset: "5px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        "&:focus": {
                          outline: "none"
                        },
                        "&:focus-visible": {
                          outline: `2px solid ${theme.customColors.headerIcon}`,
                          outlineOffset: "2px"
                        }
                      } }
                      role="button"
                      aria-label={ t("recover_pw") }
                      state={ {
                        ...routerState,
                        recover: true
                      } }
                      href="/access/password/recovery"
                    >  
                      <Typography fontSize={ "0.875rem" } fontWeight={ 500 }>{ t("recover_pw") }</Typography>
                    </Link>
                  </Stack>

                  <ButtonText
                    aria-label={ t("login") }
                    onClick={ (e) => {
                      e.preventDefault();
                      handleSubmit();
                    } }
                    type="submit"
                    sx={ {
                      gap: "8px",
                      minWidth: !authSSOIsAllowed && isMobile ? "128px" : "156px",
                      [theme.breakpoints.down("sm")]: {
                        lineHeight: "1.3125rem"
                      }
                    } }
                  >
                    { t("login") }
                  </ButtonText>
                </Stack>
              ) : null
            }

            {
              isModeSSO() && authSSOIsAllowed ? (
                <Stack alignItems={ "center" } direction={ "column" } gap={ 3 } width={ "100%" }>
                  <Divider aria-hidden="true"  sx={ {
                    "&::after": {
                      width: isMobile ? "50%" : "153px"
                    },
                    "&::before": {
                      width: isMobile ? "50%" : "153px"
                    },

                    "span": {
                      alignItems: "center",
                      display: "flex",
                      fontSize: theme.spacing(1.5),
                      height: "14px",
                      justifyContent: "center",
                      padding: theme.spacing(0, 3),
                      width: "24.187%"
                    },

                    borderColor: theme.customColors.border,
                    justifyContent: "center",
                    width: isMobile ? "100%" : "430px"
                  } }
                  >
                    { t("or") }
                  </Divider>

                  <ButtonText
                    aria-label={ `${t("sso")}` }
                    onClick={ ()=> {
                      if (ssoEndpoint) {
                        const ssoUrl = buildSSOUrlWithFromUrl(ssoEndpoint as URL | string | null, fromUrl);
                        window.location.replace(ssoUrl);
                      }
                    } }
                    sx={ {
                      gap: "8px",
                      minWidth: isMobile ? "128px" : "156px",
                      textTransform: "inherit",
                      [theme.breakpoints.down("sm")]: {
                        lineHeight: "1.3125rem"
                      }
                    } }
                  >
                    { t("sso") }
                  </ButtonText>
                </Stack>
              ) : null
            }

            { registerEnabled &&
              <Stack alignItems={ "center" } direction={ "column" } gap={ 3 } width={ "100%" }>
                <Divider sx={ {
                  "&::after": {
                    width: isMobile ? "50%" : "153px"
                  },
                  "&::before": {
                    width: isMobile ? "50%" : "153px"
                  },

                  "span": {
                    alignItems: "center",
                    display: "flex",
                    fontSize: theme.spacing(1.5),
                    height: "14px",
                    justifyContent: "center",
                    padding: theme.spacing(0, 3),
                    width: "24.187%"
                  },

                  borderColor: theme.customColors.border,
                  justifyContent: "center",
                  width: isMobile ? "100%" : "430px"
                } }
                >

                  { t("or") }
                </Divider>

                <ButtonText
                  aria-label={ t("register") }
                  onClick={ ()=> {
                    handleRegister();
                  } }
                  sx={ {
                    gap: "8px",
                    minWidth: "156px",
                    textTransform:"inherit",
                    [theme.breakpoints.down("sm")]: {
                      lineHeight: "1.3125rem"
                    }
                  } }
                >
                  { t("register") }
                </ButtonText>
              </Stack>
            }

          </Fragment> :
          <OutputMessage
            descriptionText={ descriptionText }
            labelText={ labelText }
            icon={ outputRegisterIcon }
          />


      ) }
    </Stack>

  );
}
